import { useTranslation } from '@presentation/utils';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { FaCheck } from 'react-icons/fa';
import SimpleBar from 'simplebar-react';
import { useDebounce } from '@application/utils';
import type { DropdownSelectSearchProps, SelectOption } from '..';
import { EmptyOverlay, LoadingContainer } from '..';
import * as Styled from './styled';

export * from './types';

const MAX_DROPDOWN_ITEMS_TO_SHOW_FROM_SEARCH = 20;

export const DropdownSelectSearch = <TValue,>(
  props: PropsWithChildren<DropdownSelectSearchProps<TValue>>,
) => {
  const { translate } = useTranslation();

  const {
    options = [],
    value,
    onSelect,
    wrapperProps,
    wrapperCss,
    inputProps,
    search,
    multiple = false,
    hideSearchInput = false,
    noEmpty = false,
    loading = false,
    showEmptyMessage = false,
    emptyProps,
    ...rest
  } = props;

  const showEmpty = !loading && !options.length && showEmptyMessage;

  // To avoid costly rendering of hundreds of options
  const debouncedSearch = useDebounce(search, 200);

  const showRightCheckIcon = useCallback(
    (option: SelectOption<TValue>) => {
      return multiple ? ((value as TValue[]) ?? []).includes(option.value) : value === option.value;
    },
    [multiple, value],
  );

  const optionClickHandler = useCallback(
    (option: SelectOption<TValue>) => {
      if (noEmpty && value && option.value === value) {
        return;
      }
      onSelect?.(option);
    },
    [noEmpty, onSelect, value],
  );

  const filteredOptions = useMemo(() => {
    if (debouncedSearch?.length < 2) {
      return options;
    }
    const filteredItems = options.filter((option) =>
      !debouncedSearch || option.ignoreSearch ? true : stringSearch(option, debouncedSearch) > 0,
    );
    return debouncedSearch
      ? filteredItems.splice(0, MAX_DROPDOWN_ITEMS_TO_SHOW_FROM_SEARCH)
      : filteredItems;
  }, [debouncedSearch, options]);

  return (
    <>
      {!hideSearchInput && (
        <Styled.StyledInput
          value={search ?? ''}
          placeholder={translate('common.inputs.filter.placeholder')}
          {...inputProps}
        />
      )}
      <SimpleBar style={{ maxHeight: 300 }}>
        {loading && (
          <LoadingContainer
            wrapperProps={{
              style: { maxHeight: 200, minHeight: 200 },
            }}
          />
        )}
        {showEmpty && (
          <EmptyOverlay
            icon={null}
            {...emptyProps}
            wrapperProps={{
              style: { maxHeight: 200, minHeight: 200 },
            }}
          />
        )}
        {!loading && !!options.length && (
          <Styled.OptionContainer {...rest}>
            {filteredOptions.map((option, index) => {
              const useHighlighter =
                typeof option.label === 'string' && (debouncedSearch ?? '').length > 1;
              return (
                <Styled.OptionWrapper
                  id={`option${index}`}
                  key={index}
                  index={index}
                  onClick={() => {
                    optionClickHandler(option);
                  }}
                  hasIconLeft={!!option.iconLeft}
                  hasIconRight={!!showRightCheckIcon(option) || !!option.iconRight}
                >
                  {!!option.iconLeft && (
                    <Styled.IconWrapper position="left">{option.iconLeft}</Styled.IconWrapper>
                  )}
                  <Styled.OptionLabel>
                    {useHighlighter ? (
                      <Highlighter
                        highlightClassName="highlighter"
                        autoEscape
                        textToHighlight={option.label ?? (option.value as any).toString()}
                        searchWords={(search ?? '').split('')}
                      />
                    ) : (
                      option.label
                    )}
                  </Styled.OptionLabel>
                  {showRightCheckIcon(option) && (
                    <Styled.IconWrapper position="right">
                      <FaCheck />
                    </Styled.IconWrapper>
                  )}
                  {!showRightCheckIcon(option) && !!option.iconRight && (
                    <Styled.IconWrapper position="right">{option.iconRight}</Styled.IconWrapper>
                  )}
                </Styled.OptionWrapper>
              );
            })}
          </Styled.OptionContainer>
        )}
      </SimpleBar>
    </>
  );
};

const stringSearch = (option: SelectOption<any>, search: string): number => {
  const label = option?.label?.toString() ?? '';
  const str = [...(option.searchValues ?? []), label].join(' ');
  let count = 0;
  for (let i = 0; i < str.length; i += 1) {
    for (let j = 0; j < search.length; j += 1) {
      if (search.toLowerCase()[j] !== str.toLowerCase()[i + j]) break;
      if (j === search.length - 1) count += 1;
    }
  }
  return count;
};
